// frontend/lib/hooks/useNotifications.ts
"use client";

import { useCallback, useEffect, useState } from "react";
import { apiGet, apiPost } from "../apiClient";

// =============================================================================
// Types
// =============================================================================

export type NotificationCategory =
  | "CREDENTIAL"
  | "COMPLIANCE"
  | "CONTRACT"
  | "APPLICATION"
  | "SYSTEM";

export type NotificationSeverity = "INFO" | "WARNING" | "CRITICAL";

export interface NotificationItem {
  id: string;
  category: NotificationCategory;
  type: string;
  severity: NotificationSeverity;
  title: string;
  message: string;
  metadata: Record<string, unknown>;
  is_read: boolean;
  created_at: string;
}

export interface NotificationListResponse {
  items: NotificationItem[];
  page: number;
  page_size: number;
  total: number;
  unread_count: number;
}

export interface UnreadCountResponse {
  unread_count: number;
}

// =============================================================================
// Hook
// =============================================================================

interface UseNotificationsOptions {
  /** Whether to automatically fetch notifications */
  enabled?: boolean;
  /** Polling interval in milliseconds (default: 30000) */
  pollingInterval?: number;
}

export function useNotifications(options: UseNotificationsOptions = {}) {
  const { enabled = true, pollingInterval = 30000 } = options;

  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const data = await apiGet<UnreadCountResponse>(
        "/notifications/unread-count"
      );
      setUnreadCount(data.unread_count);
    } catch (err) {
      console.error("Failed to fetch unread count:", err);
    }
  }, []);

  // Fetch notifications list
  const fetchNotifications = useCallback(async (isReadFilter?: boolean) => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (isReadFilter !== undefined) {
        params.set("is_read", String(isReadFilter));
      }
      const data = await apiGet<NotificationListResponse>(
        `/notifications?${params.toString()}`
      );
      setNotifications(data.items);
      setUnreadCount(data.unread_count);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Unknown error"));
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Mark specific notifications as read
  const markAsRead = useCallback(async (ids: string[]) => {
    try {
      await apiPost("/notifications/mark-read", { ids });
      // Update local state
      setNotifications((prev) =>
        prev.map((n) =>
          ids.includes(n.id) ? { ...n, is_read: true } : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - ids.length));
    } catch (err) {
      console.error("Failed to mark notifications as read:", err);
      throw err;
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      await apiPost("/notifications/mark-all-read", {});
      // Update local state
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, is_read: true }))
      );
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to mark all notifications as read:", err);
      throw err;
    }
  }, []);

  // Polling for unread count
  useEffect(() => {
    if (!enabled) return;

    fetchUnreadCount();

    const interval = setInterval(fetchUnreadCount, pollingInterval);
    return () => clearInterval(interval);
  }, [enabled, pollingInterval, fetchUnreadCount]);

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
  };
}
